import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IStock,
  ICreateStockPayload,
  IDeleteStockPayload,
  IStockListParams,
  IStockParams,
  IUpdateStockPayload,
} from 'entities/Stock/Stock.models';
import { stockTransport } from 'entities/Stock/Stock.transport';

interface IState {
  stock: IStock | null;
  stockList: IStock[];
  stockCount: number;
  isStockLoading: boolean;
}

const initialState: IState = {
  stock: null,
  stockList: [],
  stockCount: 0,
  isStockLoading: false,
};

export const stockSlice = createSlice({
  name: 'stock',
  initialState,
  reducers: {
    setStock(state, action: PayloadAction<IStock | null>) {
      state.stock = action.payload;
    },
    setStockList(state, action: PayloadAction<IStock[]>) {
      state.stockList = action.payload;
    },
    setStockCount(state, action: PayloadAction<number>) {
      state.stockCount = action.payload;
    },
    setIsStockLoading(state, action: PayloadAction<boolean>) {
      state.isStockLoading = action.payload;
    },
    addStockListItem(state, action: PayloadAction<IStock>) {
      state.stockList.push(action.payload);
    },
    patchStockListItem(state, action: PayloadAction<IStock>) {
      const item = state.stockList.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    removeStockListItem(state, action: PayloadAction<number>) {
      state.stockList = state.stockList.filter((item) => item.id !== action.payload);
    },
  },
});

export const {
  setStock,
  setStockList,
  setStockCount,
  setIsStockLoading,
  addStockListItem,
  patchStockListItem,
  removeStockListItem,
} = stockSlice.actions;

export const selectStock = (state: RootState) => state.stock.stock;
export const selectStockList = (state: RootState) => state.stock.stockList;
export const selectStockCount = (state: RootState) => state.stock.stockCount;
export const selectIsStockLoading = (state: RootState) => state.stock.isStockLoading;

export const stockReducer = stockSlice.reducer;

export const getStockList = (params: IStockListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsStockLoading(true));

  try {
    const response = await stockTransport.get(params);
    dispatch(setStockList(response.data));
    dispatch(setStockCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsStockLoading(false));
  }
};

export const getStockById = (params: IStockParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsStockLoading(true));

  try {
    const response = await stockTransport.getById(params.id);
    dispatch(setStock(response));
    params.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsStockLoading(false));
  }
};

export const createStock = (payload: ICreateStockPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsStockLoading(true));

  try {
    const response = await stockTransport.create(payload);
    dispatch(addStockListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsStockLoading(false));
  }
};

export const updateStock = (payload: IUpdateStockPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsStockLoading(true));

  try {
    const response = await stockTransport.update(payload);
    dispatch(patchStockListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsStockLoading(false));
  }
};

export const deleteStock = (payload: IDeleteStockPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsStockLoading(true));

  try {
    const response = await stockTransport.delete(payload);
    dispatch(removeStockListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsStockLoading(false));
  }
};
